import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", business_name: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created! Welcome to Sendora.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-7 justify-center">
          <div className="w-9 h-9 rounded-xl bg-[#0c1e41] flex items-center justify-center shadow-lg shadow-blue-600/25">
            <MessageSquare size={17} className="text-white" />
          </div>
          <span className="text-xl font-extrabold text-slate-900">Sendora</span>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <h1 className="text-2xl font-extrabold text-slate-900 mb-1">Create your account</h1>
          <p className="text-slate-500 text-sm mb-6">
            Already have an account?{" "}
            <Link to="/login" className="text-[#0c1e41] hover:text-[#0c1e41] font-semibold">Sign in</Link>
          </p>

          <form data-testid={AUTH.registerForm} onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-700 text-sm font-medium">Full Name *</Label>
              <Input
                data-testid={AUTH.registerName}
                placeholder="John Doe"
                value={form.name}
                onChange={set("name")}
                required
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 focus:border-[#0c1e41]"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 text-sm font-medium">Business Name</Label>
              <Input
                placeholder="Acme Nigeria Ltd"
                value={form.business_name}
                onChange={set("business_name")}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 focus:border-[#0c1e41]"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 text-sm font-medium">Email Address *</Label>
              <Input
                data-testid={AUTH.registerEmail}
                type="email"
                placeholder="you@company.com"
                value={form.email}
                onChange={set("email")}
                required
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 focus:border-[#0c1e41]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm font-medium">Phone</Label>
                <Input
                  type="tel"
                  placeholder="08012345678"
                  value={form.phone}
                  onChange={set("phone")}
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 focus:border-[#0c1e41]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm font-medium">Password *</Label>
                <div className="relative">
                  <Input
                    data-testid={AUTH.registerPassword}
                    type={showPass ? "text" : "password"}
                    placeholder="Min 6 chars"
                    value={form.password}
                    onChange={set("password")}
                    required
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 focus:border-[#0c1e41] pr-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            </div>

            <Button
              data-testid={AUTH.registerSubmit}
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0c1e41] hover:bg-[#091629] text-white font-semibold shadow-sm mt-1"
            >
              {loading ? (
                <><Loader2 size={16} className="mr-2 animate-spin" />Creating account...</>
              ) : (
                <>Create Account <ArrowRight size={15} className="ml-2" /></>
              )}
            </Button>

            <p className="text-xs text-slate-400 text-center">
              By creating an account, you agree to our{" "}
              <a href="#" className="text-slate-500 hover:text-[#0c1e41]">Terms</a> and{" "}
              <a href="#" className="text-slate-500 hover:text-[#0c1e41]">Privacy Policy</a>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
